"use client";

import { DownloadCard } from "@/components/marketing/download-card";

/**
 * The "see a sample" block on the /free/* document pages.
 *
 * It replaces <LeadMagnetForm>, which asked for an email address in exchange
 * for the sample. The gate held nothing back — the same PDFs are public
 * (/samples embeds all five, the homepage links the RAMS one directly) — and
 * no list, sequence or person ever used the address it collected. On
 * /free/cpp-template it also promised a "CDM compliance checklist" and then
 * emailed the sample CPP. Linking the file directly hands the visitor the
 * document at once and collects nothing.
 *
 * Renders through the shared DownloadCard (also used by the blank-template
 * block). The `document` property on `sample_downloaded` uses SamplesViewer's
 * ids (app/(marketing)/samples/samples-viewer.tsx), so the event reads the
 * same whichever page the download came from; `source` says which page.
 *
 * The map is deliberately not exported: this is a "use client" module, so a
 * server-side importer would receive a client reference, not the paths. The
 * unit test pins the paths independently.
 */
const SAMPLE_PDF_FILES = {
  rams: "/samples/sample-rams.pdf",
  cpp: "/samples/sample-cpp.pdf",
  "site-induction": "/samples/sample-site-induction.pdf",
} as const;

export type SamplePdf = keyof typeof SAMPLE_PDF_FILES;

export function SamplePdfDownload({
  sample,
  source,
  heading,
  description,
}: {
  sample: SamplePdf;
  /** PostHog `source`, e.g. "free_rams_generator". */
  source: string;
  heading: string;
  description: string;
}) {
  return (
    <DownloadCard
      heading={heading}
      description={description}
      links={[
        {
          href: SAMPLE_PDF_FILES[sample],
          label: "Download the sample PDF",
          primary: true,
          analytics: { source, document: sample },
        },
      ]}
      secondary={{ href: "/samples", label: "See all sample documents" }}
    />
  );
}
